/**
 * Cache Debug Panel
 *
 * Developer UI for inspecting and managing the cache layer.
 * Shows cache statistics, cached entries, and provides manual invalidation controls.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { cacheManager } from './cache-manager';
import { queryDeduplicationManager } from './query-deduplication-manager';

export const CacheDebugPanel = () => {
  const [stats, setStats] = useState(null);
  const [cacheKeys, setCacheKeys] = useState([]);
  const [dedupeStats, setDedupeStats] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const refreshData = useCallback(() => {
    setStats(cacheManager.getStats());
    setCacheKeys(cacheManager.getKeys());
    setDedupeStats(queryDeduplicationManager.getStats());
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(refreshData, 1000);
      return () => clearInterval(interval);
    }
    // Manual refresh when autoRefresh is off
    if (!autoRefresh) {
      // Use setTimeout to defer setState to avoid cascading renders in Effect
      setTimeout(refreshData, 0);
    }
  }, [autoRefresh, refreshData])

  const handleInvalidateAll = () => {
    cacheManager.invalidateAll();
    refreshData();
  };

  const handleInvalidateKey = (key) => {
    cacheManager.invalidateKey(key);
    refreshData();
  };

  const handleInvalidateEntity = (table) => {
    cacheManager.invalidate(table);
    refreshData();
  };

  const handleResetStats = () => {
    cacheManager.resetStats();
    queryDeduplicationManager.resetStats();
    refreshData();
  };

  const formatTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatAge = (age) => {
    if (age < 1000) return `${age}ms ago`;
    if (age < 60000) return `${Math.floor(age / 1000)}s ago`;
    return `${Math.floor(age / 60000)}m ago`;
  };

  // const groupedKeys = cacheKeys.reduce((acc, entry) => {
  //   if (!acc[entry.table]) {
  //     acc[entry.table] = [];
  //   }
  //   acc[entry.table].push(entry);
  //   return {};
  // }, {});

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 600,
        maxHeight: '90vh',
        overflow: 'auto',
        zIndex: 9999,
        boxShadow: 3
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Cache Debug Panel</Typography>
          <Chip
            label={autoRefresh ? 'Live' : 'Paused'}
            size="small"
            color={autoRefresh ? 'success' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ color: 'inherit' }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Statistics */}
        {stats && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Cache Statistics
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Hit Rate
                </Typography>
                <Typography variant="h6">{stats.hitRate}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Hits / Misses
                </Typography>
                <Typography variant="h6">
                  {stats.hits} / {stats.misses}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Cached Entries
                </Typography>
                <Typography variant="h6">{stats.size}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Deduplication Statistics */}
        {dedupeStats && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Query Deduplication
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Savings Rate
                </Typography>
                <Typography variant="h6">{dedupeStats.savingsRate}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Deduplicated
                </Typography>
                <Typography variant="h6">{dedupeStats.deduplications}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  In-Flight
                </Typography>
                <Typography variant="h6">{dedupeStats.currentInFlight}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshData}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handleResetStats}
          >
            Reset Stats
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleInvalidateAll}
          >
            Clear All Cache
          </Button>
        </Box>

        {/* Cached Entries */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {cacheKeys.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No cached entries
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entity</TableCell>
                    <TableCell>Age</TableCell>
                    <TableCell>TTL Remaining</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cacheKeys.map((entry, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: entry.isExpired ? 'error.light' : 'transparent',
                        opacity: entry.isExpired ? 0.6 : 1
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption">{entry.table}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatAge(entry.age)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {entry.isExpired ? 'Expired' : formatTime(entry.timeRemaining)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.isExpired ? 'Stale' : 'Fresh'}
                          size="small"
                          color={entry.isExpired ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleInvalidateKey(entry.key)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Entity Quick Actions */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            Quick Invalidate by Entity
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['crops', 'organizations', 'points', 'users', 'template_crop_species', 'template_crop_varieties'].map(
              (entity) => (
                <Chip
                  key={entity}
                  label={entity}
                  size="small"
                  onClick={() => handleInvalidateEntity(entity)}
                  onDelete={() => handleInvalidateEntity(entity)}
                  deleteIcon={<DeleteIcon />}
                />
              )
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
